<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="app-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalSubtitle }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.actions.modal.close"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
        <aside class="app-modal__menu" :aria-label="text.actions.modal.sectionMenuAriaLabel">
          <button
            v-for="section in sections"
            :key="section.key"
            class="app-modal__menu-item"
            :class="{ active: activeSectionKey === section.key }"
            type="button"
            @click="activeSectionKey = section.key"
          >
            <span class="app-modal__menu-icon">
              <i class="fa" :class="section.icon" aria-hidden="true"></i>
            </span>
            <span class="app-modal__menu-copy">
              <strong>{{ section.title }}</strong>
              <small>{{ section.description }}</small>
            </span>
          </button>
        </aside>

        <div class="app-modal__content">
          <form class="app-modal__form" @submit.prevent="submitForm">
            <SupplierSectionGeneral
              :active="activeSectionKey === 'general'"
              :form="form"
              :v$="v$"
              :is-edit-mode="isEditMode"
              :existing-logo-url="existingLogoUrl"
              :logo-preview-url="logoPreviewUrl"
              :logo-marked-for-delete="logoMarkedForDelete"
              :logo-file-name="logoFileName"
              :status-options="statusOptions"
              @logo-change="handleLogoChange"
              @logo-remove="handleLogoRemove"
            />

            <SupplierSectionClassification
              :active="activeSectionKey === 'classification'"
              :form="form"
              :area-options="areaOptions"
              :specialty-options="specialtyOptions"
              :show-new-specialty-input="showNewSpecialtyInput"
              :new-specialty-name="newSpecialtyName"
              :creating-new-specialty="creatingSpecialty"
              @toggle-new-specialty="toggleNewSpecialty"
              @save-new-specialty="saveNewSpecialty"
              @cancel-new-specialty="cancelNewSpecialty"
              @update:new-specialty-name="newSpecialtyName = $event"
            />

            <SupplierSectionContact
              :active="activeSectionKey === 'contact'"
              :form="form"
              :v$="v$"
            />

            <SupplierSectionAddress
              :active="activeSectionKey === 'address'"
              :form="form"
              :country-options="countryOptions"
              :state-options="stateOptions"
            />

            <footer class="app-modal__footer">
              <div class="app-modal__footer-left">
                <button
                  v-if="isEditMode"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete supplier-modal__delete-btn"
                  :title="text.actions.modal.deleteSupplier"
                  :aria-label="text.actions.modal.deleteSupplier"
                  @click="emitDelete"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <div class="app-modal__footer-right">
                <button type="button" class="btn btn-light" @click="emitClose">{{ text.actions.common.cancel }}</button>
                <button type="submit" class="btn btn-primary">{{ submitButtonLabel }}</button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { email, helpers } from '@vuelidate/validators';
import SupplierSectionGeneral from '@/components/suppliers/modal/SupplierSections/SupplierSectionGeneral.vue';
import SupplierSectionClassification from '@/components/suppliers/modal/SupplierSections/SupplierSectionClassification.vue';
import SupplierSectionContact from '@/components/suppliers/modal/SupplierSections/SupplierSectionContact.vue';
import SupplierSectionAddress from '@/components/suppliers/modal/SupplierSections/SupplierSectionAddress.vue';
import {
  createInitialSupplierForm,
  buildSupplierFormFromEntity,
  buildSupplierPayload,
  resolveSpecialtyIds,
} from '@/composables/suppliers/useSupplierModalForm';
import {
  SUPPLIER_MODAL_SECTIONS,
  loadSupplierModalCatalogs,
  resolveSupplierSpecialtiesSelection,
  createSupplierSpecialtyOption,
} from '@/composables/suppliers/useSupplierModalCatalogs';
import { resolveOption } from '@/utils/optionValues';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

export default {
  name: 'SupplierModal',
  components: {
    SupplierSectionGeneral,
    SupplierSectionClassification,
    SupplierSectionContact,
    SupplierSectionAddress,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    initialSectionKey: {
      type: String,
      default: 'general',
    },
  },
  emits: ['close', 'submit', 'delete'],
  data() {
    return {
      text,
      form: createInitialSupplierForm(),
      statusOptions: [text.modal.status.active, text.modal.status.inactive],
      activeSectionKey: 'general',
      sections: SUPPLIER_MODAL_SECTIONS,
      catalogsLoaded: false,
      catalogsLoading: false,
      areaOptions: [],
      countryOptions: [],
      stateOptions: [],
      specialtyOptions: [],
      showNewSpecialtyInput: false,
      newSpecialtyName: '',
      creatingSpecialty: false,
      logoFile: null,
      logoPreviewUrl: '',
      existingLogoUrl: '',
      logoMarkedForDelete: false,
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || 'create').toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? text.modal.editTitle : text.modal.createTitle;
    },
    modalSubtitle() {
      return text.modal.subtitle;
    },
    submitButtonLabel() {
      return this.isEditMode ? text.modal.submitEdit : text.modal.submitCreate;
    },
    logoFileName() {
      return this.logoFile instanceof File ? String(this.logoFile.name || '') : '';
    },
  },
  validations() {
    return {
      form: {
        name: {
          required: helpers.withMessage(
            text.modal.validation.nameRequired,
            (value) => helpers.req(String(value || '').trim())
          ),
        },
        classOption: {
          required: helpers.withMessage(
            text.modal.validation.classRequired,
            (value) => Number(value?.value || value || 0) > 0
          ),
        },
        categoryOption: {
          required: helpers.withMessage(
            text.modal.validation.categoryRequired,
            (value) => Number(value?.value || value || 0) > 0
          ),
        },
        email: {
          emailFormat: helpers.withMessage(
            text.modal.validation.emailInvalid,
            (value) => {
              const normalized = String(value || '').trim();
              if (!helpers.req(normalized)) {
                return true;
              }

              return email.$validator ? email.$validator(normalized) : email(normalized);
            }
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(isVisible) {
      if (isVisible) {
        this.handleModalOpen();
        return;
      }

      this.resetModalState();
    },
  },
  methods: {
    resetModalState() {
      this.form = createInitialSupplierForm();
      this.activeSectionKey = this.sections[0].key;
      this.showNewSpecialtyInput = false;
      this.newSpecialtyName = '';
      this.resetLogoState();
      this.v$.$reset();
    },
    resolveSectionKey(sectionKey) {
      const validKeys = this.sections.map((section) => section.key);
      const normalized = String(sectionKey || '').trim().toLowerCase();

      return validKeys.includes(normalized) ? normalized : this.sections[0].key;
    },
    async handleModalOpen() {
      await this.ensureCatalogsLoaded();
      this.activeSectionKey = this.resolveSectionKey(this.initialSectionKey);
      this.resetLogoState();

      if (this.isEditMode) {
        this.applyInitialDataToForm();
      } else {
        this.form = createInitialSupplierForm();
      }

      this.v$.$reset();
    },
    applyInitialDataToForm() {
      const formFromEntity = buildSupplierFormFromEntity(this.initialData || {});

      this.form = {
        ...createInitialSupplierForm(),
        ...formFromEntity,
        status: resolveOption(this.statusOptions, formFromEntity.status) || text.modal.status.active,
        classOption: formFromEntity.classOption,
        categoryOption: formFromEntity.categoryOption,
        areaOption: resolveOption(this.areaOptions, formFromEntity.areaOption),
        specialties: resolveSupplierSpecialtiesSelection(formFromEntity.specialties, this.specialtyOptions),
        country: resolveOption(this.countryOptions, formFromEntity.country),
        state: resolveOption(this.stateOptions, formFromEntity.state),
      };

      const currentLogoUrl = String(this.initialData?.logo_thumb_url || this.initialData?.logo_url || '').trim();
      this.existingLogoUrl = currentLogoUrl;
      this.logoMarkedForDelete = false;
    },
    async ensureCatalogsLoaded() {
      if (this.catalogsLoaded || this.catalogsLoading) {
        return;
      }

      this.catalogsLoading = true;

      try {
        const catalogs = await loadSupplierModalCatalogs();
        this.specialtyOptions = catalogs.specialtyOptions;
        this.areaOptions = catalogs.areaOptions;
        this.countryOptions = catalogs.countryOptions;
        this.stateOptions = catalogs.stateOptions;
        this.catalogsLoaded = true;
      } catch (error) {
        this.specialtyOptions = [];
        this.areaOptions = [];
        this.countryOptions = [];
        this.stateOptions = [];
        this.catalogsLoaded = false;
      } finally {
        this.catalogsLoading = false;
      }
    },
    toggleNewSpecialty() {
      this.showNewSpecialtyInput = !this.showNewSpecialtyInput;
      if (!this.showNewSpecialtyInput) {
        this.newSpecialtyName = '';
      }
    },
    async saveNewSpecialty() {
      const name = String(this.newSpecialtyName || '').trim();
      if (name === '' || this.creatingSpecialty) {
        return;
      }

      this.creatingSpecialty = true;
      try {
        const createdOption = await createSupplierSpecialtyOption(name);

        if (createdOption) {
          this.specialtyOptions = [...this.specialtyOptions, createdOption];
          this.form.specialties = [
            ...(Array.isArray(this.form.specialties) ? this.form.specialties : []),
            createdOption,
          ];
        }

        this.showNewSpecialtyInput = false;
        this.newSpecialtyName = '';
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || text.toast.createSpecialtyError;
        this.$toast?.error(message);
      } finally {
        this.creatingSpecialty = false;
      }
    },
    cancelNewSpecialty() {
      this.showNewSpecialtyInput = false;
      this.newSpecialtyName = '';
    },
    emitClose() {
      this.$emit('close');
    },
    emitDelete() {
      this.$emit('delete', this.initialData || null);
    },
    revokeLogoPreview() {
      if (!this.logoPreviewUrl) {
        return;
      }

      URL.revokeObjectURL(this.logoPreviewUrl);
      this.logoPreviewUrl = '';
    },
    resetLogoState() {
      this.revokeLogoPreview();
      this.logoFile = null;
      this.existingLogoUrl = '';
      this.logoMarkedForDelete = false;
    },
    handleLogoChange(file) {
      const nextFile = file instanceof File ? file : null;

      this.revokeLogoPreview();
      this.logoFile = nextFile;

      if (!nextFile) {
        return;
      }

      this.logoPreviewUrl = URL.createObjectURL(nextFile);
      this.logoMarkedForDelete = false;
    },
    handleLogoRemove() {
      if (this.logoFile instanceof File) {
        this.logoFile = null;
        this.revokeLogoPreview();
        return;
      }

      if (!this.isEditMode) {
        return;
      }

      this.logoMarkedForDelete = true;
    },
    submitForm() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        if (this.v$.form.name.$error || this.v$.form.classOption.$error || this.v$.form.categoryOption.$error) {
          this.activeSectionKey = 'general';
        } else if (this.v$.form.email.$error) {
          this.activeSectionKey = 'contact';
        }

        return;
      }

      const payload = buildSupplierPayload(this.form);
      const specialtyIds = resolveSpecialtyIds(this.form);

      if (this.logoFile instanceof File) {
        payload.logo = this.logoFile;
      }

      if (this.isEditMode && this.logoMarkedForDelete && !(this.logoFile instanceof File)) {
        payload.delete_logo = 1;
      }

      this.$emit('submit', {
        mode: this.isEditMode ? 'edit' : 'create',
        id: Number(this.initialData?.id || 0),
        payload,
        specialtyIds,
      });
    },
  },
  beforeUnmount() {
    this.revokeLogoPreview();
  },
};
</script>

<style scoped>
.app-modal__footer-left {
  margin-right: auto;
}

.supplier-modal__delete-btn {
  min-height: 38px;
  height: 38px;
  width: 44px;
  min-width: 44px;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>

